import * as React from "react";

export interface VideoProps {
  videoUrl: string;
  ref?: React.RefObject<HTMLVideoElement> | null;
  controls?: boolean;
  type?: string;
  autoplay?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  loop?: boolean;
  classes?: string;
  aspectRatio?: string;
  objectFit?: 'contain' | 'cover' | 'fill';
  sizingMode?: 'aspect_ratio' | 'custom_height' | 'responsive';
  customHeight?: number;
  borderRadius?: number;
}

export const Video = ({
  videoUrl,
  ref = null,
  controls = false,
  type = 'video/mp4',
  autoplay = false,
  muted = true,
  playsInline = true,
  loop = true,
  classes = "",
  aspectRatio = "16:9",
  objectFit = "cover",
  sizingMode = "aspect_ratio",
  customHeight,
  borderRadius = 0
}: VideoProps) => {
    // Generate styles based on props
  const containerStyle: React.CSSProperties = {};
  const videoStyle: React.CSSProperties = {
    width: '100%',
    objectFit
  };

  // Apply border radius
  if (borderRadius > 0) {
    containerStyle.borderRadius = `${borderRadius}px`;
    containerStyle.overflow = 'hidden';
    videoStyle.borderRadius = `${borderRadius}px`;
  }

  // Apply sizing based on mode
  if (sizingMode === 'aspect_ratio') {
    // Calculate aspect ratio for padding-bottom
    let paddingBottom = '56.25%'; // Default 16:9
    
    switch (aspectRatio) {
      case '1:1':
        paddingBottom = '100%';
        break;
      case '4:3':
        paddingBottom = '75%';
        break;
      case '9:16':
        paddingBottom = '177.78%';
        break;
      case '21:9':
        paddingBottom = '42.86%';
        break;
      default: 
        paddingBottom = '56.25%'; // 16:9
    }
    
    containerStyle.position = 'relative';
    containerStyle.paddingBottom = paddingBottom;
    containerStyle.height = 0;
    containerStyle.overflow = 'hidden';
    
    videoStyle.position = 'absolute';
    videoStyle.top = 0;
    videoStyle.left = 0;
    videoStyle.width = '100%';
    videoStyle.height = '100%';
  } else if (sizingMode === 'custom_height' && customHeight) {
    containerStyle.height = `${customHeight}px`;
    videoStyle.height = '100%';
  }

  return (
    <div className={`video-container ${classes}`} style={containerStyle}>
      <video 
        ref={ref} 
        style={videoStyle}
        autoPlay={autoplay} 
        muted={muted || autoplay} // Always mute if autoplay is enabled
        loop={loop} 
        controls={controls}
        playsInline={playsInline}
      >
        <source src={videoUrl} type={type}/>
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

export default Video;
